import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './scheduling-components.css';

interface ShiftTemplate {
  templateId: string;
  templateName: string;
  templateType: 'daily' | 'weekly' | 'custom';
  shifts: TemplateShift[];
  isDefault: boolean;
  effectiveDate: string;
  expirationDate?: string;
  createdAt: string;
  updatedAt: string;
}

interface TemplateShift {
  shiftId: string;
  shiftName: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  department: 'FOH' | 'BOH' | 'MANAGEMENT';
  jobCode: string;
  requiredStaff: number;
  breakMinutes: number;
}

interface ShiftTemplatesProps {
  storeId: string;
  enterpriseId: string;
}

export const ShiftTemplates: React.FC<ShiftTemplatesProps> = ({ 
  storeId, 
  enterpriseId 
}) => {
  const [templates, setTemplates] = useState<ShiftTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'daily' | 'weekly' | 'custom'>('weekly');
  
  const [newTemplate, setNewTemplate] = useState({
    templateName: '',
    templateType: 'weekly' as const,
    shifts: [] as TemplateShift[],
    isDefault: false,
    effectiveDate: new Date().toISOString().split('T')[0]
  });

  const [newShift, setNewShift] = useState({
    shiftName: '',
    dayOfWeek: 1,
    startTime: '09:00',
    endTime: '17:00',
    department: 'FOH' as const,
    jobCode: '',
    requiredStaff: 1,
    breakMinutes: 30
  });

  useEffect(() => {
    fetchTemplates();
  }, [storeId, enterpriseId]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_ENDPOINTS.employees()}/shift-templates?storeId=${storeId}&enterpriseId=${enterpriseId}`
      );
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.employees()}/shift-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newTemplate,
          storeId,
          enterpriseId
        })
      });

      if (response.ok) {
        setShowCreateModal(false);
        resetNewTemplate();
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const response = await fetch(`${API_ENDPOINTS.employees()}/shift-templates/${templateId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const handleSetDefault = async (templateId: string) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.employees()}/shift-templates/${templateId}/set-default`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId, enterpriseId })
      });

      if (response.ok) {
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error setting default template:', error);
    }
  };

  const addShiftToTemplate = () => {
    const shiftId = `shift-${Date.now()}`;
    setNewTemplate({
      ...newTemplate,
      shifts: [...newTemplate.shifts, { ...newShift, shiftId }]
    });
    // Reset shift form
    setNewShift({
      shiftName: '',
      dayOfWeek: 1,
      startTime: '09:00',
      endTime: '17:00',
      department: 'FOH',
      jobCode: '',
      requiredStaff: 1,
      breakMinutes: 30
    });
  };

  const removeShiftFromTemplate = (shiftId: string) => {
    setNewTemplate({
      ...newTemplate,
      shifts: newTemplate.shifts.filter(s => s.shiftId !== shiftId)
    });
  };

  const resetNewTemplate = () => {
    setNewTemplate({
      templateName: '',
      templateType: 'weekly',
      shifts: [],
      isDefault: false,
      effectiveDate: new Date().toISOString().split('T')[0]
    });
  };

  const getDayName = (dayOfWeek: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek];
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (loading) {
    return <div className="loading">Loading shift templates...</div>;
  }

  return (
    <div className="shift-templates">
      <div className="page-header">
        <h2>Shift Templates</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          Create Template
        </button>
      </div>

      <div className="template-tabs">
        <button 
          className={`tab ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          Daily Templates
        </button>
        <button 
          className={`tab ${activeTab === 'weekly' ? 'active' : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          Weekly Templates
        </button>
        <button 
          className={`tab ${activeTab === 'custom' ? 'active' : ''}`}
          onClick={() => setActiveTab('custom')}
        >
          Custom Templates
        </button>
      </div>

      {templates.filter(t => t.templateType === activeTab).length === 0 ? (
        <div className="empty-state">
          <h3>No {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Templates</h3>
          <p>Create reusable shift templates to streamline scheduling.</p>
          <button 
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            Create First Template
          </button>
        </div>
      ) : (
        <div className="templates-grid">
          {templates.filter(t => t.templateType === activeTab).map((template) => (
            <div key={template.templateId} className="template-card">
              <div className="template-header">
                <h3>{template.templateName}</h3>
                {template.isDefault && <span className="default-badge">Default</span>}
              </div>
              <div className="template-info">
                <p className="template-type">{template.templateType.charAt(0).toUpperCase() + template.templateType.slice(1)} Template</p>
                <p className="shift-count">{template.shifts.length} shifts configured</p>
                <p className="effective-date">Effective: {new Date(template.effectiveDate).toLocaleDateString()}</p>
              </div>
              <div className="template-shifts">
                <h4>Shifts:</h4>
                {template.shifts.slice(0, 3).map((shift, idx) => (
                  <div key={idx} className="shift-preview">
                    <span className="shift-day">{template.templateType === 'weekly' ? getDayName(shift.dayOfWeek) : 'Daily'}</span>
                    <span className="shift-time">{formatTime(shift.startTime)} - {formatTime(shift.endTime)}</span>
                    <span className="shift-dept">{shift.department}</span>
                  </div>
                ))}
                {template.shifts.length > 3 && (
                  <p className="more-shifts">+{template.shifts.length - 3} more shifts</p>
                )}
              </div>
              <div className="template-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    // Edit functionality to be implemented
                    console.log('Edit template:', template.templateId);
                  }}
                >
                  Edit
                </button>
                {!template.isDefault && (
                  <button 
                    className="btn btn-secondary"
                    onClick={() => handleSetDefault(template.templateId)}
                  >
                    Set as Default
                  </button>
                )}
                <button 
                  className="btn btn-danger"
                  onClick={() => handleDeleteTemplate(template.templateId)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h3>Create Shift Template</h3>
              <button onClick={() => {
                setShowCreateModal(false);
                resetNewTemplate();
              }}>×</button>
            </div>
            <div className="modal-content">
              <div className="template-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Template Name *</label>
                    <input
                      type="text"
                      value={newTemplate.templateName}
                      onChange={(e) => setNewTemplate({...newTemplate, templateName: e.target.value})}
                      placeholder="e.g., Standard Weekly Schedule"
                    />
                  </div>
                  <div className="form-group">
                    <label>Template Type *</label>
                    <select
                      value={newTemplate.templateType}
                      onChange={(e) => setNewTemplate({...newTemplate, templateType: e.target.value as any})}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Effective Date *</label>
                    <input
                      type="date"
                      value={newTemplate.effectiveDate}
                      onChange={(e) => setNewTemplate({...newTemplate, effectiveDate: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={newTemplate.isDefault}
                        onChange={(e) => setNewTemplate({...newTemplate, isDefault: e.target.checked})}
                      />
                      Set as default template
                    </label>
                  </div>
                </div>

                <div className="shifts-section">
                  <h4>Add Shifts to Template</h4>
                  <div className="shift-form">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Shift Name</label>
                        <input
                          type="text"
                          value={newShift.shiftName}
                          onChange={(e) => setNewShift({...newShift, shiftName: e.target.value})}
                          placeholder="e.g., Morning Shift"
                        />
                      </div>
                      {newTemplate.templateType === 'weekly' && (
                        <div className="form-group">
                          <label>Day of Week</label>
                          <select
                            value={newShift.dayOfWeek}
                            onChange={(e) => setNewShift({...newShift, dayOfWeek: parseInt(e.target.value)})}
                          >
                            <option value={0}>Sunday</option>
                            <option value={1}>Monday</option>
                            <option value={2}>Tuesday</option>
                            <option value={3}>Wednesday</option>
                            <option value={4}>Thursday</option>
                            <option value={5}>Friday</option>
                            <option value={6}>Saturday</option>
                          </select>
                        </div>
                      )}
                      <div className="form-group">
                        <label>Start Time</label>
                        <input
                          type="time"
                          value={newShift.startTime}
                          onChange={(e) => setNewShift({...newShift, startTime: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>End Time</label>
                        <input
                          type="time"
                          value={newShift.endTime}
                          onChange={(e) => setNewShift({...newShift, endTime: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Department</label>
                        <select
                          value={newShift.department}
                          onChange={(e) => setNewShift({...newShift, department: e.target.value as any})}
                        >
                          <option value="FOH">Front of House</option>
                          <option value="BOH">Back of House</option>
                          <option value="MANAGEMENT">Management</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Required Staff</label>
                        <input
                          type="number"
                          min="1"
                          value={newShift.requiredStaff}
                          onChange={(e) => setNewShift({...newShift, requiredStaff: parseInt(e.target.value) || 1})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Break Minutes</label>
                        <input
                          type="number"
                          min="0"
                          value={newShift.breakMinutes}
                          onChange={(e) => setNewShift({...newShift, breakMinutes: parseInt(e.target.value) || 0})}
                        />
                      </div>
                      <div className="form-group">
                        <button 
                          type="button"
                          className="btn btn-secondary"
                          onClick={addShiftToTemplate}
                        >
                          Add Shift
                        </button>
                      </div>
                    </div>
                  </div>

                  {newTemplate.shifts.length > 0 && (
                    <div className="added-shifts">
                      <h5>Added Shifts ({newTemplate.shifts.length})</h5>
                      <div className="shifts-list">
                        {newTemplate.shifts.map((shift) => (
                          <div key={shift.shiftId} className="shift-item">
                            <div className="shift-details">
                              <span className="shift-name">{shift.shiftName || 'Unnamed Shift'}</span>
                              {newTemplate.templateType === 'weekly' && (
                                <span className="shift-day">{getDayName(shift.dayOfWeek)}</span>
                              )}
                              <span className="shift-time">{formatTime(shift.startTime)} - {formatTime(shift.endTime)}</span>
                              <span className="shift-dept">{shift.department}</span>
                              <span className="shift-staff">{shift.requiredStaff} staff</span>
                            </div>
                            <button 
                              className="remove-shift"
                              onClick={() => removeShiftFromTemplate(shift.shiftId)}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowCreateModal(false);
                  resetNewTemplate();
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleCreateTemplate}
                disabled={!newTemplate.templateName || newTemplate.shifts.length === 0}
              >
                Create Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};